/**
 * Voice provider catalog for the AI receptionist's text-to-speech (TTS) stage.
 *
 * Tenants pick a GENERIC voice option (e.g. Female/Male) that is stored on their business profile; operators pick the PLATFORM-WIDE TTS provider in the Portal global settings. At call time the
 * voice service resolves (provider, option) through this catalog into the concrete vendor `voiceId` stamped onto the transient assistant. The indirection means a provider switch (or a vendor
 * retiring a voice) never invalidates any tenant's stored choice - remapping one catalog entry here fixes every tenant at once.
 *
 * The `Record<VoiceProvider, Record<VoiceOption, ...>>` totality is the safety contract: adding a provider without mapping every option (or an option without mapping every provider) is a compile
 * error, so any tenant selection can always resolve under any platform provider.
 */

/**
 * Generic, provider-agnostic voice options a tenant can pick from. Each gender offers a default plus a brighter (higher-energy) and a deeper (lower/calmer) variant; the concrete vendor voice is
 * resolved per provider at call time.
 */
export const VoiceOptionValues = ['Female', 'FemaleBright', 'FemaleDeep', 'Male', 'MaleDeep', 'MaleBright'] as const;

/** A generic tenant-facing voice option. */
export type VoiceOption = (typeof VoiceOptionValues)[number];

/**
 * TTS providers selectable as the platform-wide voice vendor. All are Vapi-orchestrated; no extra vendor key is required (Vapi bills the TTS stage per minute). `elevenhigh` and `elevenfast` are
 * BOTH the ElevenLabs vendor (`11labs` at the Vapi boundary); they are exposed as two separate platform providers because they differ only by the pinned TTS model - elevenhigh runs
 * `eleven_multilingual_v2` (highest-quality multilingual) and elevenfast runs `eleven_flash_v2_5` (lowest-latency multilingual). See `vendorVoiceConfigByProvider`.
 */
export const VoiceProviderValues = ['openai', 'azure', 'elevenhigh', 'elevenfast', 'vapi'] as const;

/** A supported TTS provider slug (the value sent as `voice.provider` on the transient assistant). */
export type VoiceProvider = (typeof VoiceProviderValues)[number];

/** A concrete vendor voice resolved from a generic option. */
export interface IVoiceSelection {
  provider: VoiceProvider;
  voiceId: string;
}

/** Operator-facing metadata describing a TTS provider's strengths and weaknesses. */
export interface IVoiceProviderData {
  Provider: VoiceProvider;
  Label: string;
  // Short operator-facing summary of quality / cost / speed / language posture, surfaced as the Portal tooltip.
  Description: string;
}

/** The platform default generic voice option, used when a tenant has not picked one (or a stored value is no longer valid). */
export const DefaultVoiceOption: VoiceOption = 'FemaleBright';

/**
 * The platform default TTS provider, used when global settings carry no (or invalid) provider. ElevenLabs gives the most natural, human-like delivery, accepting its higher per-minute cost;
 * elevenhigh pins the highest-quality multilingual model. Eleven Multilingual v2 is better for high-quality, pre-recorded audio. Eleven Flash v2_5 is better for conversational AI and live voice
 * agents because of its ultra-low latency.
 */
export const DefaultVoiceProvider: VoiceProvider = 'elevenfast';

/**
 * The platform default MULTILINGUAL TTS provider, used for tenants whose primary locale is not English when global settings carry no (or invalid) multilingual provider. Azure's
 * `*MultilingualNeural` voices pronounce non-English far better than the standard provider and cover 140+ languages with enterprise voice-lifecycle guarantees, so they are the safe default for
 * non-English tenants while elevenfast stays the standard provider for the English-primary majority.
 */
export const DefaultMultilingualVoiceProvider: VoiceProvider = 'azure';

/**
 * Maps every (provider, option) pair to a concrete vendor voiceId. Every entry is a long-standing, popular voice chosen to minimize retirement risk, sanity-checked against the live vendor
 * catalogs:
 *
 * - Openai: stable for years across TTS releases; 50+ languages from any voice. Options use the standard tts-1 set (nova/shimmer/fable, onyx/alloy/echo); the per-call quality vs latency tier is
 *   the OpenAI TTS MODEL chosen in the voice service, not the voiceId.
 * - Azure: Microsoft neural voices with enterprise lifecycle guarantees; 140+ languages across the catalog. Every option is a `*MultilingualNeural` voice that natively speaks the caller's language
 *   from a single voice (auto-detected mid-call), so a caller who switches from English to Spanish keeps the same voice identity instead of getting an English-only `en-US-*Neural` voice
 *   mispronouncing the words. These are the highest-quality multilingual Azure voices; the locale-pinned `en-US-*Neural` voices are lower latency but English-only.
 * - Elevenhigh / elevenfast: ElevenLabs flagship default-library voices (identical voiceIds across both). Both run on the single ElevenLabs vendor (`11labs`) and are fully multilingual; they
 *   differ ONLY by the model pinned at the Vapi boundary (see `vendorVoiceConfigByProvider`) - elevenhigh uses `eleven_multilingual_v2` (highest quality, slightly higher latency) and elevenfast
 *   uses `eleven_flash_v2_5` (lowest latency, small quality cost). NOTE: the legacy Rachel default (21m00Tcm4TlvDq8ikWAM) was retired from the premade library, so Female now maps to Sarah; Adam
 *   remains valid. Eleven Multilingual v2 is better for high-quality, pre-recorded audio. Eleven Flash v2_5 is better for conversational AI and live voice agents because of its ultra-low
 *   latency.
 * - Vapi: Vapi's curated in-house voices (cheapest, bundled), but effectively English-only and with the HIGHEST catalog churn of any provider - a whole batch (Spencer, Neha, Harry, Cole, Paige,
 *   Hana, Lily, Kylie) was retired on 2026-03-01. These map to Vapi's current "Active Voices" set: Elliot and Savannah are the longest-lived defaults; every other slot deliberately uses a
 *   newest-generation "New V2" voice, which is least likely to be retired next. Because Vapi churns aggressively, REVIEW.md carries a recurring task to re-confirm these names against
 *   https://docs.vapi.ai/providers/voice/vapi-voices and remap any that retire, always preferring the newest generation.
 *
 * @internal
 */
const voiceMap: Record<VoiceProvider, Record<VoiceOption, IVoiceSelection>> = {
  openai: {
    Female: { provider: 'openai', voiceId: 'nova' },
    FemaleBright: { provider: 'openai', voiceId: 'shimmer' },
    FemaleDeep: { provider: 'openai', voiceId: 'fable' },
    Male: { provider: 'openai', voiceId: 'onyx' },
    MaleDeep: { provider: 'openai', voiceId: 'alloy' },
    MaleBright: { provider: 'openai', voiceId: 'echo' }
  },
  azure: {
    Female: { provider: 'azure', voiceId: 'en-US-AvaMultilingualNeural' }, // Warm, natural flagship multilingual voice
    FemaleBright: { provider: 'azure', voiceId: 'en-US-EmmaMultilingualNeural' }, // Lighter, brighter multilingual voice
    FemaleDeep: { provider: 'azure', voiceId: 'en-US-JennyMultilingualNeural' }, // Mature, steady multilingual voice
    Male: { provider: 'azure', voiceId: 'en-US-AndrewMultilingualNeural' }, // Warm, natural multilingual voice
    MaleDeep: { provider: 'azure', voiceId: 'en-US-AdamMultilingualNeural' }, // Calm, deeper multilingual voice (replaces the Brian reference)
    MaleBright: { provider: 'azure', voiceId: 'en-US-RyanMultilingualNeural' } // Energetic, brighter multilingual voice
  },
  elevenhigh: {
    Female: { provider: 'elevenhigh', voiceId: 'EXAVITQu4vr4xnSDxMaL' }, // Sarah (replaces retired Rachel)
    FemaleBright: { provider: 'elevenhigh', voiceId: 'cgSgspJ2msm6clMCkdW9' }, // Jessica
    FemaleDeep: { provider: 'elevenhigh', voiceId: 'pFZP5JQG7iQjIQuC4Bku' }, // Lily
    Male: { provider: 'elevenhigh', voiceId: 'pNInz6obpgDQGcFmaJgB' }, // Adam
    MaleDeep: { provider: 'elevenhigh', voiceId: 'nPczCjzI2devNBz1zQrb' }, // Brian
    MaleBright: { provider: 'elevenhigh', voiceId: 'TX3LPaxmHKxFdv7VOQHJ' } // Liam
  },
  elevenfast: {
    // Same ElevenLabs voiceIds as elevenhigh; the eleven_flash_v2_5 model (lower latency) is pinned at the Vapi boundary, not here.
    Female: { provider: 'elevenfast', voiceId: 'EXAVITQu4vr4xnSDxMaL' }, // Sarah (replaces retired Rachel)
    FemaleBright: { provider: 'elevenfast', voiceId: 'cgSgspJ2msm6clMCkdW9' }, // Jessica
    FemaleDeep: { provider: 'elevenfast', voiceId: 'pFZP5JQG7iQjIQuC4Bku' }, // Lily
    Male: { provider: 'elevenfast', voiceId: 'pNInz6obpgDQGcFmaJgB' }, // Adam
    MaleDeep: { provider: 'elevenfast', voiceId: 'nPczCjzI2devNBz1zQrb' }, // Brian
    MaleBright: { provider: 'elevenfast', voiceId: 'TX3LPaxmHKxFdv7VOQHJ' } // Liam
  },
  vapi: {
    Female: { provider: 'vapi', voiceId: 'Savannah' }, // Active V2, default keep-list voice (American Southern)
    FemaleBright: { provider: 'vapi', voiceId: 'Layla' }, // New V2 (warm, bright, cheerful)
    FemaleDeep: { provider: 'vapi', voiceId: 'Clara' }, // New V2 (warm, professional)
    Male: { provider: 'vapi', voiceId: 'Elliot' }, // Active V2, default keep-list voice (Canadian)
    MaleDeep: { provider: 'vapi', voiceId: 'Sid' }, // New V2 (laid-back, smooth, deep-toned)
    MaleBright: { provider: 'vapi', voiceId: 'Godfrey' } // New V2 (young, energetic)
  }
};

/** The concrete Vapi vendor voice config a platform provider resolves to at the Vapi boundary. */
interface IVendorVoiceConfig {
  // The vendor provider slug Vapi expects on `voice.provider` (e.g. '11labs' for both ElevenLabs tiers).
  provider: string;
  // The vendor TTS model pinned on `voice.model` when the tier is model-gated. Omitted when the provider has no model field (azure/vapi) or uses its vendor default (openai).
  model?: string;
}

/**
 * Maps each PLATFORM provider to its concrete Vapi VENDOR config. The platform exposes elevenhigh and elevenfast as two selectable providers, but both run on the single ElevenLabs vendor
 * (`11labs`) and differ ONLY by the pinned model: elevenhigh uses `eleven_multilingual_v2` (highest-quality multilingual) and elevenfast uses `eleven_flash_v2_5` (lowest-latency multilingual).
 * openai, azure and vapi map 1:1 to their own vendor slug with no model pin (the vendor default applies).
 *
 * Eleven Multilingual v2 is better for high-quality, pre-recorded audio. Eleven Flash v2_5 is better for conversational AI and live voice agents because of its ultra-low latency.
 *
 * @internal
 */
const vendorVoiceConfigByProvider: Record<VoiceProvider, IVendorVoiceConfig> = {
  openai: { provider: 'openai' },
  azure: { provider: 'azure' },
  elevenhigh: { provider: '11labs', model: 'eleven_multilingual_v2' },
  elevenfast: { provider: '11labs', model: 'eleven_flash_v2_5' },
  vapi: { provider: 'vapi' }
};

/** A concrete Vapi voice payload: the vendor provider slug, the voiceId, and the optional vendor TTS model. */
export interface IVendorVoiceSelection {
  provider: string;
  voiceId: string;
  model?: string;
}

/**
 * Translates an internal (platform) voice selection into the concrete Vapi voice payload, mapping the platform provider to its vendor slug and pinning the vendor TTS model where the tier
 * requires one. This is the ONLY place the platform/vendor split is resolved, so both the live call (VoiceWebhookController) and the health probe (VoiceHealthService) stamp identical voice
 * objects.
 *
 * @param selection - The internal voice selection (platform provider + voiceId).
 * @returns The Vapi voice payload (vendor provider + voiceId + optional model).
 */
export function ResolveVendorVoice(selection: IVoiceSelection): IVendorVoiceSelection {
  const config = vendorVoiceConfigByProvider[selection.provider] ?? vendorVoiceConfigByProvider[DefaultVoiceProvider];
  return config.model ? { provider: config.provider, voiceId: selection.voiceId, model: config.model } : { provider: config.provider, voiceId: selection.voiceId };
}

/**
 * Operator-facing provider catalog with tooltip descriptions, in recommendation order.
 *
 * @internal
 */
const voiceProviderData: IVoiceProviderData[] = [
  {
    Provider: 'elevenfast',
    Label: 'ElevenLabs (Fast)',
    Description:
      'Recommended default. The same ElevenLabs voices on the eleven_flash_v2_5 model: still multilingual and very natural, with much lower latency (faster to begin speaking) at a small quality cost. Best when responsiveness matters more than absolute fidelity.'
  },
  {
    Provider: 'elevenhigh',
    Label: 'ElevenLabs (Quality)',
    Description:
      'Best raw voice quality and the most natural, human-like delivery, 30+ languages, on the eleven_multilingual_v2 model. The most premium option (highest per-minute cost) and slightly slower to begin speaking.'
  },
  {
    Provider: 'openai',
    Label: 'OpenAI',
    Description: 'Consistent quality, cheap, fast, 50+ languages from every voice, and the most stable voice catalog (unchanged for years).'
  },
  {
    Provider: 'azure',
    Label: 'Azure',
    Description:
      "Recommended non-English default. Widest language coverage (140+ languages) with enterprise-grade voice lifecycle guarantees. Uses multilingual neural voices that speak the caller's language natively. Very good quality, mid cost, mid speed."
  },
  {
    Provider: 'vapi',
    Label: 'Vapi',
    Description: 'Cheapest (bundled into Vapi per-minute pricing) and fast, but effectively English-only and with the highest catalog churn (8 voices retired in 2026).'
  }
];

/** @returns The operator-facing TTS provider catalog, in recommendation order. */
export function GetVoiceProviderList(): IVoiceProviderData[] {
  return voiceProviderData;
}

/**
 * Retrieves the operator-facing metadata for a TTS provider.
 *
 * @param provider - The provider slug (e.g. from global settings; tolerates unknown/stale values).
 * @returns The provider metadata, falling back to the platform default provider's entry.
 */
export function GetVoiceProviderData(provider: string): IVoiceProviderData {
  return voiceProviderData.find((entry) => entry.Provider === provider) ?? voiceProviderData[0];
}

/**
 * Resolves a tenant's generic voice option into a concrete vendor voice under the given platform provider. Both inputs tolerate unknown/stale values (fail-open to platform defaults) so a call
 * can never fail because a stored choice no longer exists.
 *
 * @param provider - The platform TTS provider slug (e.g. from global settings).
 * @param option - The tenant's stored generic voice option.
 * @param overrides - Optional operator-overridden voice mappings from global settings. Overrides take precedence over the hardcoded catalog.
 * @returns The concrete vendor voice selection.
 */
export function ResolveVoiceSelection(provider: string, option: string, overrides?: Record<string, Record<string, string>>): IVoiceSelection {
  const providerKey = (VoiceProviderValues as readonly string[]).includes(provider) ? (provider as VoiceProvider) : DefaultVoiceProvider;
  const optionKey = (VoiceOptionValues as readonly string[]).includes(option) ? (option as VoiceOption) : DefaultVoiceOption;
  const overrideVoiceId = overrides?.[providerKey]?.[optionKey];
  if (overrideVoiceId && overrideVoiceId.length > 0) {
    return { provider: providerKey, voiceId: overrideVoiceId };
  }
  return voiceMap[providerKey][optionKey];
}

/**
 * Returns the hardcoded default voice catalog as a flat list of entries. Used by the Portal Voices page to display the baseline catalog.
 *
 * @returns Array of default voice catalog entries.
 */
export function GetDefaultVoiceCatalog(): { provider: VoiceProvider; option: VoiceOption; voiceId: string }[] {
  const result: { provider: VoiceProvider; option: VoiceOption; voiceId: string }[] = [];
  for (const provider of VoiceProviderValues) {
    for (const option of VoiceOptionValues) {
      result.push({ provider, option, voiceId: voiceMap[provider][option].voiceId });
    }
  }
  return result;
}

/**
 * Resolves the cross-provider fallback voices for the same generic option: the SAME gender/style from EVERY other vendor, in recommendation order, stamped into the transient assistant's
 * `voice.fallbackPlan.voices` so a vendor-side voice failure (a retired voiceId, or a whole-provider outage) degrades to a comparable voice instead of failing the call. Listing all remaining
 * providers (rather than just one) means the call keeps a working voice as long as ANY single vendor in the catalog is healthy - so a retired voiceId we have not yet re-synced can be survived by
 * the next vendor down the list.
 *
 * @param primaryProvider - The resolved primary provider slug (excluded from the fallback list).
 * @param option - The tenant's stored generic voice option.
 * @param overrides - Optional operator-overridden voice mappings from global settings.
 * @returns The concrete fallback voice selections from every other provider, in recommendation order.
 */
export function ResolveVoiceFallbacks(primaryProvider: string, option: string, overrides?: Record<string, Record<string, string>>): IVoiceSelection[] {
  return voiceProviderData.filter((entry) => entry.Provider !== primaryProvider).map((entry) => ResolveVoiceSelection(entry.Provider, option, overrides));
}
